import React, { useState, useEffect, useRef } from "react";
import { RefreshCw } from "lucide-react";
import { configService } from "../services/configService";
import toast from "react-hot-toast";
import SupplierShipmentScheduler from "../components/SupplierShipmentScheduler";
import { formatDate } from "../utils/dateUtils";
import { isTokenInvalidationError, isLoggingOut } from "../utils/errorUtils";

interface SyncStatus {
  total_shipments: number;
  synced_shipments: number;
  pending_sync: number;
  recent_synced_shipments: number;
  last_synced_at: string | null;
  is_syncing?: boolean;
}

interface SyncLogItem {
  id: number;
  supplier_shipment_id: number | null;
  shipment_code: string;
  shipment_name: string;
  action: string;
  sync_direction: string;
  status: string;
  error_message?: string | null;
  changed_fields?: string | null; // JSON string
  request_data?: string | null; // JSON or text
  response_data?: string | null; // JSON or text
  synced_at: string | null;
  created_at: string | null;
  mode: string;
}

interface SupplierShipmentItem {
  id: number;
  mode: string;
  fulfil_id: number | null;
  number: string;
  supplier_name: string;
  shiphero_po_id: number | null;
  shiphero_po_legacy_id?: number | string | null;
  sync_status: string;
  sync_error?: string | null;
  retry_count?: number;
  needs_manual_retry?: boolean;
  planned_date?: string | null;
  create_date?: string | null;
  acknowledged_at?: string | null;
  // Derived flags from backend for UI badges
  acknowledged?: boolean;
  failed?: boolean;
  received?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
}

const SupplierShipmentsPage: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    total_shipments: 0,
    synced_shipments: 0,
    pending_sync: 0,
    recent_synced_shipments: 0,
    last_synced_at: null,
    is_syncing: false,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRunningSync, setIsRunningSync] = useState(false);
  const [perPage] = useState(20);
  const [query, setQuery] = useState("");
  const [syncMode, setSyncMode] = useState<"live" | "test">("live");
  const [isModeLoading, setIsModeLoading] = useState(true);
  const [isUpdatingMode, setIsUpdatingMode] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<NodeJS.Timeout | null>(null);

  // Shipments listing state
  const [shipments, setShipments] = useState<SupplierShipmentItem[]>([]);
  const [shipmentsPage, setShipmentsPage] = useState(1);
  const [shipmentsTotalPages, setShipmentsTotalPages] = useState(1);
  const [isShipmentsLoading, setIsShipmentsLoading] = useState(true);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyShipmentNumber, setHistoryShipmentNumber] = useState<string | null>(null);
  const [historyLogs, setHistoryLogs] = useState<SyncLogItem[]>([]);
  const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  
  // Button loading states
  const [loadingRetrySync, setLoadingRetrySync] = useState<Set<number>>(new Set());
  const [loadingSyncNow, setLoadingSyncNow] = useState<Set<string>>(new Set());
  const prevQueryRef = useRef(query);
  const autoRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadSyncStatus();
    loadSyncMode();
  }, []);

  useEffect(() => {
    // Load shipments after sync mode has been loaded
    if (syncMode && !isModeLoading) {
       const didQueryChange = prevQueryRef.current !== query;
      prevQueryRef.current = query;
      if (didQueryChange && shipmentsPage !== 1) {
        setShipmentsPage(1);
        return;
      }

      loadShipments(shipmentsPage, query);
    }
  }, [shipmentsPage, query, syncMode, isModeLoading]);

  // Monitor mode changes and reload logs when mode changes
  useEffect(() => {
    if (syncMode && !isModeLoading) {
      console.log("Mode changed to:", syncMode, "- reloading shipments");
      setShipmentsPage(1);
      loadShipments(1, query);
      loadSyncStatus();
    }
  }, [syncMode]);

  // Auto-refresh during sync operations
  useEffect(() => {
      const isSyncing = isRunningSync || Boolean(syncStatus.is_syncing);
  
      if (!isSyncing) {
        if (autoRefreshIntervalRef.current) {
          clearInterval(autoRefreshIntervalRef.current);
          autoRefreshIntervalRef.current = null;
        }
        return;
      }
  
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
  
      autoRefreshIntervalRef.current = setInterval(async () => {
        await loadSyncStatus();
        if (syncMode && !isModeLoading) {
          await loadShipments(shipmentsPage, query);
        }
      }, 5000);
  
      return () => {
        if (autoRefreshIntervalRef.current) {
          clearInterval(autoRefreshIntervalRef.current);
          autoRefreshIntervalRef.current = null;
        }
      };
    }, [isRunningSync, syncStatus.is_syncing, syncMode, isModeLoading, shipmentsPage, query]);

  const loadSyncStatus = async () => {
    try {
      const status = await configService.getSupplierShipmentSyncStatus();
      setSyncStatus(status);
    } catch (error) {
      console.error("Failed to load supplier shipment sync status:", error);
    }
  };

  // Logs table removed; per-shipment history is available via modal

  const loadShipments = async (pageNum: number, q?: string) => {
    try {
      setIsShipmentsLoading(true);
      const res = await configService.listSupplierShipments(pageNum, perPage, q, syncMode);
      setShipments(res.items);
      setShipmentsTotalPages(res.total_pages || 1);
    } catch (error) {
      console.error("Failed to load supplier shipments:", error);
    } finally {
      setIsShipmentsLoading(false);
    }
  };

  const loadSyncMode = async () => {
    try {
      setIsModeLoading(true);
      const modeConfig = await configService.getSupplierShipmentSyncMode();
      console.log("Loaded sync mode:", modeConfig.mode);
      setSyncMode(modeConfig.mode);
    } catch (error) {
      console.error("Failed to load sync mode:", error);
    } finally {
      setIsModeLoading(false);
    }
  };

  const handleModeChange = async (newMode: "live" | "test") => {
    if (newMode === syncMode) return;
    try {
      setIsUpdatingMode(true);
      await configService.updateSupplierShipmentSyncMode(newMode);
      setSyncMode(newMode);
      toast.success(`Supplier shipment sync mode set to ${newMode.toUpperCase()}`);
      // Reload shipments for the new mode
      setShipmentsPage(1);
      await loadShipments(1, query);
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message ||"Failed to update sync mode");
      }
      console.error("Failed to update sync mode:", error);
    } finally {
      setIsUpdatingMode(false);
    }
  };

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error("Please enter a value to search");
      return;
    }

    setShipmentsPage(1);
    await loadShipments(1, query);
  };

  const clearSearch = async () => {
    setQuery("");
    setShipmentsPage(1);
    await loadShipments(1, "");
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await loadSyncStatus();
      await loadShipments(shipmentsPage, query);
    } catch (error) {
      console.error("Failed to refresh data:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRunNow = async () => {
    setIsRunningSync(true);
    try {
      const res = await configService.runSupplierShipmentSync();
      toast.success(res.message || "Supplier shipment sync completed");
      await loadSyncStatus();
      await loadShipments(shipmentsPage, query);
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to run supplier shipment sync");
      }
    } finally {
      setIsRunningSync(false);
    }
  };

  const openHistory = async (shipmentNumber: string) => {
    try {
      setShowHistoryModal(true);
      setHistoryShipmentNumber(shipmentNumber);
      setExpandedLogs(new Set());
      setIsHistoryLoading(true);
      const res = await configService.getSupplierShipmentHistory(shipmentNumber, syncMode);
      setHistoryLogs(res.items as any);
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to load shipment history");
      }
      setShowHistoryModal(false);
      setHistoryShipmentNumber(null);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const manualSyncReceipts = async (shipmentNumber: string) => {
    // Add to loading set
    setLoadingSyncNow(prev => new Set(prev).add(shipmentNumber));
    
    try {
      const res = await configService.syncShipHeroReceiptsForShipment(shipmentNumber);
      if ((res as any).status === "error") {
        toast.error((res as any).message || "Manual receipt sync failed");
      } else {
        toast.success((res as any).message || "Manual receipt sync completed successfully");
      }
      await refreshData();
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to run manual receipt sync");
      }
    } finally {
      // Remove from loading set
      setLoadingSyncNow(prev => {
        const newSet = new Set(prev);
        newSet.delete(shipmentNumber);
        return newSet;
      });
    }
  };

  const retryShipmentSync = async (shipmentId: number) => {
    // Add to loading set
    setLoadingRetrySync(prev => new Set(prev).add(shipmentId));
    
    try {
      const res = await configService.retrySupplierShipmentSync(shipmentId);
      if ((res as any).status === "error") {
        toast.error((res as any).message || "Manual retry failed");
      } else {
        toast.success((res as any).message || "Manual retry completed successfully");
      }
      await refreshData();
    } catch (error: any) {
      if (!isTokenInvalidationError(error) && !isLoggingOut()) {
        toast.error(error?.message || "Failed to retry shipment sync");
      }
    } finally {
      // Remove from loading set
      setLoadingRetrySync(prev => {
        const newSet = new Set(prev);
        newSet.delete(shipmentId);
        return newSet;
      });
    }
  };


  // Formatting helpers for cleaner labels in the history modal
  const toTitleCase = (s: string) => {
    if (!s) return s;
    return s
      .replace(/[_-]+/g, " ")
      .split(" ")
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(" ");
  };

  const formatDirection = (d?: string) => {
    if (!d) return "";
    if (d === "fulfil_to_shiphero") return "Fulfil → ShipHero";
    if (d === "shiphero_to_fulfil") return "ShipHero → Fulfil";
    return toTitleCase(d);
  };

  const formatStatus = (s?: string) => {
    if (!s) return "";
    return toTitleCase(s);
  };

  // Removed logs-related helpers and state

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Supplier Shipments</h1>
          <p className="mt-2 text-sm text-gray-700">
            Monitor synchronization of supplier shipments between Fulfil and ShipHero
          </p>
        </div>
        {/* Actions Bar */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          {/* Mode control */}
          <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-md border">
            <span className="text-sm font-medium text-gray-700">Mode</span>
            {isModeLoading ? (
              <div className="inline-flex items-center px-3 py-1.5 text-sm text-gray-500">
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </div>
            ) : (
              <div className="inline-flex rounded border overflow-hidden">
                <button
                  onClick={() => handleModeChange("live")}
                  disabled={isUpdatingMode || isRunningSync || syncStatus.is_syncing}
                  className={`${
                    syncMode === "live"
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  } px-3 py-1.5 text-sm font-medium focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Live
                </button>
                <button
                  onClick={() => handleModeChange("test")}
                  disabled={isUpdatingMode || isRunningSync || syncStatus.is_syncing}
                  className={`${
                    syncMode === "test"
                      ? "bg-primary-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  } px-3 py-1.5 text-sm font-medium border-l focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Test
                </button>
              </div>
            )}
            {isUpdatingMode && (
              <RefreshCw className="ml-1 h-4 w-4 animate-spin text-primary-600" />
            )}
          </div>

          {/* Run Now - Consolidated with sync status */}
          <button
            onClick={handleRunNow}
            disabled={isRunningSync || syncStatus.is_syncing}
            className={`inline-flex items-center justify-center px-3 py-2 text-sm rounded-md border ${
              isRunningSync || syncStatus.is_syncing
                ? "border-yellow-600 text-yellow-700 bg-yellow-50"
                : "border-primary-600 text-primary-700 bg-white hover:bg-primary-50"
            } disabled:opacity-50`}
          >
            {isRunningSync || syncStatus.is_syncing ? (
              <span className="inline-flex items-center">
                <RefreshCw className="h-5 w-5 mr-2 animate-spin" /> Sync Running...
              </span>
            ) : (
              <span>Run Now</span>
            )}
          </button>

          {/* Refresh */}
          <button
            onClick={refreshData}
            disabled={isRefreshing}
            className="inline-flex items-center px-3 py-2 rounded-md text-sm border bg-white hover:bg-gray-50"
          >
            <RefreshCw
              className={`h-5 w-5 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Sync Scheduler section */}
      <SupplierShipmentScheduler />

      {/* Filters row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Search supplier shipments"
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <div className="flex items-center gap-2">
          <button
            onClick={handleSearch}
            className="px-3 py-2 text-sm rounded-md border bg-white hover:bg-gray-50"
          >
            Search
          </button>
          <button
            onClick={clearSearch}
            className="px-3 py-2 text-sm rounded-md border bg-white hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Sync Status Summary */}
      <div className="text-sm text-gray-700">
        <span className="mr-6">
          <span className="text-gray-500">Total Shipments:</span>{" "}
          <span className="font-semibold">{syncStatus.total_shipments}</span>
        </span>
        <span className="mr-6">
          <span className="text-gray-500">Synced:</span>{" "}
          <span className="font-semibold">{syncStatus.synced_shipments}</span>
        </span>
        <span className="mr-6">
          <span className="text-gray-500">Pending:</span>{" "}
          <span className="font-semibold">{syncStatus.pending_sync}</span>
        </span>
        <span>
          <span className="text-gray-500">Last Sync:</span>{" "}
          <span className="font-semibold">
            {formatDate(syncStatus.last_synced_at)}
          </span>
        </span>
      </div>

      {/* Supplier Shipments Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Supplier Shipments</h3>
            {!isModeLoading && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Showing shipments for:</span>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  syncMode === "live" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"
                }`}>
                  {syncMode.toUpperCase()} mode
                </span>
              </div>
            )}
          </div>

          {isModeLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading sync mode...</p>
              </div>
            </div>
          ) : isShipmentsLoading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="h-8 w-8 text-primary-600 animate-spin" />
            </div>
          ): shipments.length === 0 ? (
            <div className="text-center py-10 text-gray-500">No shipments found</div>
          ) : (
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Number</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Supplier</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">ShipHero PO ID</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Created</th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {shipments.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{s.number}</td>
                      <td className="px-3 py-4 text-sm text-gray-700">
                        <div className="max-w-32 truncate" title={s.supplier_name}>
                          {s.supplier_name}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm">
                        <div className="flex items-center gap-2">
                          <span 
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer ${
                              s.sync_status === "pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : s.sync_status === "synced"
                                ? "bg-green-100 text-green-800"
                                : s.sync_status === "error"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                            title={s.sync_error || `Status: ${s.sync_status || "unknown"}`}
                          >
                            {s.sync_status || "unknown"}
                          </span>
                          {s.acknowledged && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="Acknowledged in Fulfil">
                              Acknowledged
                            </span>
                          )}
                          {s.received && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800" title="Received items detected">
                              Received
                            </span>
                          )}
                          {s.failed && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" title="Failure detected">
                              Failed
                            </span>
                          )}
                          {(s.retry_count && s.retry_count > 0) ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                              {s.retry_count} {s.retry_count === 1 ? 'retry' : 'retries'}
                            </span>
                          ) : (<></>)}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{s.shiphero_po_legacy_id ?? "-"}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700">{formatDate(s.created_at || null)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-right text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => openHistory(s.number)}
                            className="px-2 py-1 border rounded hover:bg-gray-50"
                          >
                            View
                          </button>
                          {s.sync_error ? (
                            <button
                              onClick={() => retryShipmentSync(s.id)}
                              disabled={loadingRetrySync.has(s.id)}
                              className={`px-2 py-1 border rounded text-xs font-medium ${loadingRetrySync.has(s.id)
                                  ? 'bg-red-100 text-red-400 cursor-not-allowed border-red-200'
                                  : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                              }`}
                            >
                              {loadingRetrySync.has(s.id) ? (
                                <div className="flex items-center">
                                  <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                                  Syncing...
                                </div>
                              ) : (
                                'Retry Sync'
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={() => manualSyncReceipts(s.number)}
                              disabled={loadingSyncNow.has(s.number)}
                              className={`px-2 py-1 border rounded text-xs font-medium ${loadingSyncNow.has(s.number)
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                  : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                              }`}
                            >
                              {loadingSyncNow.has(s.number) ? (
                                <div className="flex items-center">
                                  <RefreshCw className="h-3 w-3 animate-spin mr-1" />
                                  Syncing...
                                </div>
                              ) : (
                                'Sync Now'
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Pagination */}
          {shipmentsTotalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => {
                    const p = Math.max(1, shipmentsPage - 1);
                    setShipmentsPage(p);
                    loadShipments(p, query);
                  }}
                  disabled={shipmentsPage <= 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => {
                    const p = Math.min(shipmentsTotalPages, shipmentsPage + 1);
                    setShipmentsPage(p);
                    loadShipments(p, query);
                  }}
                  disabled={shipmentsPage >= shipmentsTotalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{shipmentsPage}</span> of{" "}
                    <span className="font-medium">{shipmentsTotalPages}</span>
                  </p>
                </div>
                <div>
                  <nav
                    className="isolate inline-flex -space-x-px rounded-md shadow-sm"
                    aria-label="Pagination"
                  >
                    <button
                      onClick={() => {
                        const p = Math.max(1, shipmentsPage - 1);
                        setShipmentsPage(p);
                        loadShipments(p, query);
                      }}
                      disabled={shipmentsPage <= 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    {(() => {
                      const buttons = [] as number[];
                      const start = Math.max(1, Math.min(shipmentsPage - 2, Math.max(1, shipmentsTotalPages - 4)));
                      const end = Math.min(shipmentsTotalPages, start + 4);
                      for (let p = start; p <= end; p++) buttons.push(p);
                      return buttons;
                    })().map((p) => (
                      <button
                        key={p}
                        onClick={() => {
                          setShipmentsPage(p);
                          loadShipments(p, query);
                        }}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${p === shipmentsPage
                            ? "z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                            : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                          }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        const p = Math.min(shipmentsTotalPages, shipmentsPage + 1);
                        setShipmentsPage(p);
                        loadShipments(p, query);
                      }}
                      disabled={shipmentsPage >= shipmentsTotalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* History Modal */}
      {showHistoryModal && historyShipmentNumber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Sync History</h3>
                <p className="text-sm text-gray-600">Shipment: {historyShipmentNumber}</p>
              </div>
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setHistoryShipmentNumber(null);
                  setHistoryLogs([]);
                  setExpandedLogs(new Set());
                }}
                className="px-4 py-2 text-sm rounded-md border bg-white hover:bg-gray-50 font-medium"
              >
                Close
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {isHistoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <RefreshCw className="h-8 w-8 text-primary-600 animate-spin mx-auto mb-2" />
                    <div className="text-gray-500 font-medium">Loading history...</div>
                  </div>
                </div>
              ) : historyLogs.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-lg mb-2">📋</div>
                  <div className="text-gray-500 font-medium">No history found</div>
                  <div className="text-gray-400 text-sm">This shipment has no sync history records.</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Status</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Action</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Direction</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Mode</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Created</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-left">Synced</th>
                        <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wider text-right">Details</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {historyLogs.map((log) => {
                        const isOpen = expandedLogs.has(log.id);
                        const parseMaybeJson = (val?: string | null) => {
                          if (!val) return null;
                          try { return JSON.parse(val); } catch { return val; }
                        };
                        const changed = parseMaybeJson(log.changed_fields);
                        const req = parseMaybeJson(log.request_data);
                        const res = parseMaybeJson(log.response_data);
                        return (
                          <React.Fragment key={log.id}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  log.status === 'success' ? 'bg-green-100 text-green-800' :
                                  log.status === 'error' ? 'bg-red-100 text-red-800' :
                                  log.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {formatStatus(log.status)}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">{toTitleCase(log.action)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700">{formatDirection(log.sync_direction)}</td>
                              <td className="px-4 py-2 text-sm text-gray-700">{toTitleCase(log.mode)}</td>
                              <td className="px-4 py-2 text-xs text-gray-500">{formatDate(log.created_at)}</td>
                              <td className="px-4 py-2 text-xs text-gray-500">{log.synced_at ? formatDate(log.synced_at) : '—'}</td>
                              <td className="px-4 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => setExpandedLogs(prev => {
                                    const next = new Set(prev);
                                    if (next.has(log.id)) next.delete(log.id); else next.add(log.id);
                                    return next;
                                  })}
                                  className="inline-flex items-center px-3 py-1 text-sm border rounded-md bg-white hover:bg-gray-50"
                                  aria-expanded={isOpen}
                                >
                                  {isOpen ? 'Hide' : 'View'}
                                </button>
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-gray-50">
                                <td className="px-4 py-3" colSpan={7}>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                                    <div>
                                      <span className="font-medium text-gray-700">Shipment Code:</span>
                                      <span className="ml-2 text-gray-900">{log.shipment_code || 'N/A'}</span>
                                    </div>
                                    <div>
                                      <span className="font-medium text-gray-700">Shipment Name:</span>
                                      <span className="ml-2 text-gray-900">{log.shipment_name || 'N/A'}</span>
                                    </div>
                                    {log.supplier_shipment_id && (
                                      <div>
                                        <span className="font-medium text-gray-700">Supplier Shipment ID:</span>
                                        <span className="ml-2 text-gray-900">{log.supplier_shipment_id}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="space-y-4">
                                    {changed && (
                                      <div>
                                        <div className="font-medium text-gray-700 mb-2">Changed Fields</div>
                                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 max-h-56 overflow-auto overflow-x-auto">
                                          {typeof changed === 'string' ? (
                                            <pre className="text-xs text-blue-800 whitespace-pre-wrap break-words font-mono">{changed}</pre>
                                          ) : (
                                            <pre className="text-xs text-blue-800 whitespace-pre font-mono">{JSON.stringify(changed, null, 2)}</pre>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {req && (
                                      <div>
                                        <div className="font-medium text-gray-700 mb-2">Request Data</div>
                                        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 max-h-56 overflow-auto overflow-x-auto">
                                          {typeof req === 'string' ? (
                                            <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words font-mono">{req}</pre>
                                          ) : (
                                            <pre className="text-xs text-gray-800 whitespace-pre font-mono">{JSON.stringify(req, null, 2)}</pre>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {res && (
                                      <div>
                                        <div className="font-medium text-gray-700 mb-2">Response Data</div>
                                        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 max-h-56 overflow-auto overflow-x-auto">
                                          {typeof res === 'string' ? (
                                            <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words font-mono">{res}</pre>
                                          ) : (
                                            <pre className="text-xs text-gray-800 whitespace-pre font-mono">{JSON.stringify(res, null, 2)}</pre>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {log.error_message && (
                                      <div>
                                        <div className="font-medium text-red-700 mb-2">Error Details</div>
                                        <div className="bg-red-50 border border-red-200 rounded-md p-3 overflow-auto">
                                          <pre className="text-xs text-red-800 whitespace-pre-wrap break-words font-mono">{log.error_message}</pre>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierShipmentsPage;
